import axios from 'axios'
import { determineAppType } from '@utils/appDetection'

// API and Storage base URLs
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8000/api'
export const STORAGE_BASE_URL = API_BASE_URL.replace('/api', '')

// Helper to get full storage URL for images/files
export const getStorageUrl = (path) => {
  if (!path) return null
  if (path.startsWith('http')) return path
  const cleanPath = path.startsWith('/') ? path : `/${path}`
  return `${STORAGE_BASE_URL}${cleanPath}`
}

// Create axios instance
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
})

// Request interceptor to add auth token and tenant host info
api.interceptors.request.use(
  (config) => {
    // Get token based on app type
    const appType = determineAppType()
    const tokenKey = getTokenKey(appType)
    const token = localStorage.getItem(tokenKey)

    if (token) {
      config.headers.Authorization = `Bearer ${token}`
    }

    // For tenant websites, pass the original host to backend for tenant resolution
    if (appType === 'tenant' || appType === 'marketing') {
      config.headers['X-Original-Host'] = window.location.hostname
    }

    // If sending FormData, let axios set the Content-Type automatically
    if (config.data instanceof FormData) {
      delete config.headers['Content-Type']
    }

    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    const { response } = error

    if (response?.status === 401) {
      // Token expired or invalid
      handleAuthError()
    } else if (response?.status === 403) {
      // Permission denied
      console.warn('Access denied:', response.data.message)
    } else if (response?.status >= 500) {
      // Server error
      console.error('Server error:', response.data.message)
    }

    return Promise.reject(error)
  }
)

// Helper functions - determineAppType is now imported at the top

const getTokenKey = (appType) => {
  switch (appType) {
    case 'super-admin':
      return 'super_admin_token'
    case 'tenant':
      // Check current path to determine user type
      const path = window.location.pathname
      if (path.startsWith('/admin')) return 'tenant_admin_token'
      if (path.startsWith('/user')) return 'tenant_admin_token'
      if (path.startsWith('/state-official')) return 'state_official_token'
      return 'tenant_token'
    default:
      return 'auth_token'
  }
}

const handleAuthError = () => {
  // Clear all tokens
  const tokenKeys = [
    'super_admin_token',
    'tenant_admin_token', 
    'state_official_token',
    'tenant_token',
    'auth_token'
  ]
  
  tokenKeys.forEach(key => localStorage.removeItem(key))
  
  // Redirect to appropriate login page
  const appType = determineAppType()
  switch (appType) {
    case 'super-admin':
      window.location.href = '/login'
      break
    case 'tenant':
      const path = window.location.pathname
      if (path.startsWith('/admin')) {
        window.location.href = '/admin/login'
      } else if (path.startsWith('/user')) {
        window.location.href = '/user-login'
      } else if (path.startsWith('/state-official')) {
        window.location.href = '/state-official/login'
      } else {
        window.location.href = '/'
      }
      break
    default:
      window.location.href = '/'
  }
}

// API service methods
export const apiService = {
  // Generic HTTP methods
  get: (url, config = {}) => api.get(url, config),
  post: (url, data = {}, config = {}) => api.post(url, data, config),
  put: (url, data = {}, config = {}) => api.put(url, data, config),
  patch: (url, data = {}, config = {}) => api.patch(url, data, config),
  delete: (url, config = {}) => api.delete(url, config),

  // File upload
  upload: (url, formData, onUploadProgress = null) => {
    return api.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
      onUploadProgress,
    })
  },

  // Download file
  download: (url, filename) => {
    return api.get(url, {
      responseType: 'blob',
    }).then(response => {
      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename
      document.body.appendChild(link)
      link.click()
      link.remove()
      window.URL.revokeObjectURL(downloadUrl)
    })
  },

  // Health check
  health: () => api.get('/health'),
}

export default api